import { useState } from 'react';
import { Ticket, Gift, Crown, Trophy, X, Copy, ExternalLink, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminVouchersTab({ vouchers, fetchAllData }) {
  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [voucherForm, setVoucherForm] = useState({
    code: '',
    voucher_type: 'premium_trial',
    value: 30,
    max_uses: '',
    expires_at: '',
    description: ''
  });

  const createVoucher = async () => {
    try {
      await axios.post(`${API}/api/admin/vouchers`, {
        ...voucherForm,
        max_uses: voucherForm.max_uses ? parseInt(voucherForm.max_uses) : null,
        value: parseInt(voucherForm.value)
      });
      toast.success("Voucher created!");
      setShowVoucherForm(false);
      setVoucherForm({ code: '', voucher_type: 'premium_trial', value: 30, max_uses: '', expires_at: '', description: '' });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create voucher");
    }
  };

  const toggleVoucherStatus = async (id, currentStatus) => {
    try {
      const response = await axios.patch(`${API}/api/admin/vouchers/${id}/toggle`);
      toast.success(response.data.is_active ? "Voucher activated" : "Voucher deactivated");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to update voucher");
    }
  };

  const deleteVoucher = async (id) => {
    if (!window.confirm("Delete this voucher?")) return;
    try {
      await axios.delete(`${API}/api/admin/vouchers/${id}`);
      toast.success("Voucher deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete voucher");
    }
  };

  return (
    <div className="space-y-4">
      {/* Voucher Form */}
      {showVoucherForm && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-brand" /> Create Voucher Code
            </h3>
            <button onClick={() => setShowVoucherForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input
                type="text"
                value={voucherForm.code}
                onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., WELCOME2024"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={voucherForm.voucher_type}
                onChange={(e) => {
                  const type = e.target.value;
                  let defaultValue = type === "premium_trial" ? 30 : type === "contributor_points" ? 25 : 10;
                  setVoucherForm({ ...voucherForm, voucher_type: type, value: defaultValue });
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              >
                <option value="premium_trial">Premium Trial (days free)</option>
                <option value="premium_discount">Premium Discount (%)</option>
                <option value="contributor_points">Contributor Points</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value ({voucherForm.voucher_type === "premium_trial" ? "days" : voucherForm.voucher_type === "premium_discount" ? "%" : "points"})
              </label>
              <input
                type="number"
                value={voucherForm.value}
                onChange={(e) => setVoucherForm({ ...voucherForm, value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
              {voucherForm.voucher_type === "contributor_points" && (
                <div className="flex gap-2 mt-2">
                  {[25, 50, 100].map(pts => (
                    <button
                      key={pts}
                      type="button"
                      onClick={() => setVoucherForm({ ...voucherForm, value: pts })}
                      className={`px-3 py-1 text-xs rounded-full ${voucherForm.value === pts ? 'bg-brand text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {pts} pts
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (optional)</label>
              <input
                type="number"
                value={voucherForm.max_uses}
                onChange={(e) => setVoucherForm({ ...voucherForm, max_uses: e.target.value })}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires At (optional)</label>
              <input
                type="date"
                value={voucherForm.expires_at}
                onChange={(e) => setVoucherForm({ ...voucherForm, expires_at: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={voucherForm.description}
                onChange={(e) => setVoucherForm({ ...voucherForm, description: e.target.value })}
                placeholder="e.g., 1 month free trial"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setShowVoucherForm(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={createVoucher} disabled={!voucherForm.code} className="btn-primary">Create Voucher</button>
          </div>
        </div>
      )}
      
      {/* Vouchers List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Gift className="w-5 h-5 text-brand" /> Voucher Codes ({vouchers.length})
            </h3>
            <p className="text-sm text-gray-500 mt-1">Create promo codes for premium trials and contributor bonus points</p>
          </div>
          <button onClick={() => setShowVoucherForm(true)} className="btn-primary">+ New Voucher</button>
        </div>
        
        {vouchers.length === 0 ? (
          <div className="p-8 text-center">
            <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900">No vouchers yet</h3>
            <p className="text-gray-500 mt-1">Create your first promo code</p>
            <button onClick={() => setShowVoucherForm(true)} className="btn-primary mt-4">Create Voucher</button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {vouchers.filter(v => v.is_active).map((voucher) => (
              <div key={voucher.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-1.5 bg-gray-900 text-amber-400 rounded-lg font-mono text-sm font-bold">
                        {voucher.code}
                      </code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(voucher.code); toast.success("Code copied!"); }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { 
                          const url = `https://strider.work/welcome?voucher=${voucher.code}`;
                          navigator.clipboard.writeText(url); 
                          toast.success("URL copied: " + url); 
                        }}
                        className="p-1.5 text-brand hover:text-brand-dark hover:bg-brand/10 rounded flex items-center gap-1"
                        title="Copy shareable URL"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span className="text-xs font-medium">URL</span>
                      </button>
                    </div>
                    <div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        voucher.voucher_type === 'premium_trial' ? 'bg-amber-100 text-amber-700' :
                        voucher.voucher_type === 'premium_discount' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {voucher.voucher_type === 'premium_trial' ? `${voucher.value} days free` :
                         voucher.voucher_type === 'premium_discount' ? `${voucher.value}% off` :
                         `${voucher.value} points`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{voucher.uses_count || 0}{voucher.max_uses ? `/${voucher.max_uses}` : ''} used</span>
                    {voucher.expires_at && (
                      <span className={new Date(voucher.expires_at) < new Date() ? 'text-red-500' : ''}>
                        Expires: {new Date(voucher.expires_at).toLocaleDateString()}
                      </span>
                    )}
                    <button
                      onClick={() => toggleVoucherStatus(voucher.id, voucher.is_active)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        voucher.is_active 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      title={voucher.is_active ? "Click to deactivate" : "Click to activate"}
                    >
                      {voucher.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => deleteVoucher(voucher.id)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {voucher.description && (
                  <p className="text-sm text-gray-500 mt-1">{voucher.description}</p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-400">URL:</span>
                  <code className="text-xs text-brand bg-brand/5 px-2 py-0.5 rounded">
                    strider.work/welcome?voucher={voucher.code}
                  </code>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Quick Templates */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h4 className="font-medium text-gray-900 mb-3">Quick Create Templates</h4>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setVoucherForm({ code: `TRIAL${Date.now().toString(36).toUpperCase()}`, voucher_type: 'premium_trial', value: 30, max_uses: 1, expires_at: '', description: '1 month free premium trial' }); setShowVoucherForm(true); }}
            className="px-3 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100"
          >
            <Crown className="w-4 h-4 inline mr-1" /> 1 Month Free Trial
          </button>
          <button
            onClick={() => { setVoucherForm({ code: `POINTS25${Date.now().toString(36).toUpperCase()}`, voucher_type: 'contributor_points', value: 25, max_uses: '', expires_at: '', description: '25 bonus points' }); setShowVoucherForm(true); }}
            className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100"
          >
            <Trophy className="w-4 h-4 inline mr-1" /> 25 Points
          </button>
          <button
            onClick={() => { setVoucherForm({ code: `POINTS50${Date.now().toString(36).toUpperCase()}`, voucher_type: 'contributor_points', value: 50, max_uses: '', expires_at: '', description: '50 bonus points' }); setShowVoucherForm(true); }}
            className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100"
          >
            <Trophy className="w-4 h-4 inline mr-1" /> 50 Points
          </button>
          <button
            onClick={() => { setVoucherForm({ code: `POINTS100${Date.now().toString(36).toUpperCase()}`, voucher_type: 'contributor_points', value: 100, max_uses: '', expires_at: '', description: '100 bonus points' }); setShowVoucherForm(true); }}
            className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100"
          >
            <Trophy className="w-4 h-4 inline mr-1" /> 100 Points
          </button>
        </div>
      </div>
    </div>
  );
}
